import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import Layout from '@/layouts';
import { NextPageWithLayout } from '../_app';
import { page3FaqJsonLd } from '@/lib/jsonld';
import { getFaqQuestions } from '@/components/RecentFollow/FAQSection/FaqData';
import { AllSupportLanguages } from '@/shared/language';
import { getRevalidateTime, SHOW_ISR_DEBUG_PANEL, SITE_HOST, ISR_ENV } from '@/shared/isr-config';
import ISRDebugPanel from '@/components/Common/ISRDebugPanel';

const RecentFollow = dynamic(() => import('@/components/RecentFollow'), {
  ssr: true,
});

const PAGE_PATH = '/instagram-new-follow-tracker';

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;

  const [localeRes, tdkRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(PAGE_PATH, locale),
  ]);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  // 使用 SITE_HOST 替代 req.headers.host
  const siteUrl = locale !== 'en' ? `https://${SITE_HOST}/${locale}` : `https://${SITE_HOST}`;

  // 创建一个简单的翻译函数，用于在服务端获取FAQ数据
  const t = (key: string) => tempData[key] || key;

  // 获取FAQ问题数据用于page3FaqJsonLd
  const faqQuestions = getFaqQuestions(t);

  const jsonLd = page3FaqJsonLd(
    {
      title: 'Instagram New Follow Tracker',
      desc: tdkRes?.data?.description ?? tempData['head_Recentfollow_Meta_Desc'],
      url: canonicalUrl(SITE_HOST, PAGE_PATH, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    { url: siteUrl },
    faqQuestions,
    {
      name: 'Instagram Recent Follow Tracker – DolphinRadar',
      url: 'https://www.dolphinradar.com/instagram-new-follow-tracker',
      applicationCategory: 'Instagram Follower Tracker',
      description:
        'See who someone recently followed on Instagram without login. Track new Instagram followers and following anonymously.',
      featureList: [
        'See Recently Followed on Instagram',
        'See New Following Anonymously',
        'Track Recent Followers',
        'No Instagram Login Required',
        'Latest Data',
        'Instant Results',
      ],
    }
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(SITE_HOST, PAGE_PATH, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData['head_Recentfollow_Meta_Title'],
      description: tdkRes?.data?.description ?? tempData['head_Recentfollow_Meta_Desc'],
      jsonLd,
      // ISR 调试信息
      __isr_generated_at: Date.now(),
      __isr_revalidate: getRevalidateTime('instagram-new-follow-tracker'),
      __isr_page_name: 'instagram-new-follow-tracker',
      __isr_env: ISR_ENV,
    },
    revalidate: getRevalidateTime('instagram-new-follow-tracker'),
  };
};

const IndexPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return useMemo(
    () => (
      <>
        <RecentFollow />
        {SHOW_ISR_DEBUG_PANEL && (
          <ISRDebugPanel
            pageName={props.__isr_page_name}
            generatedAt={props.__isr_generated_at}
            revalidate={props.__isr_revalidate}
            env={props.__isr_env}
          />
        )}
      </>
    ),
    [props]
  );
};

IndexPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}

          {AllSupportLanguages.map((lang) => (
            <link
              key={lang.code}
              rel="alternate"
              hrefLang={lang.code}
              href={`https://www.dolphinradar.com/${lang.code}/instagram-new-follow-tracker`}
            />
          ))}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default IndexPage;
